import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";

import { PersonHolderForm } from "./holder/PersonHolderForm";

type CreateHolderModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void;
};

const PERSON_FORM_ID = "create-holder-person-form";

/**
 * A card holder is always a natural person, so this modal is a single form with no type selector.
 *
 * The business half was removed from HERE only. `BusinessHolderForm` is not dead code — the KYB step of
 * `/app/onboarding` still renders it (`verification/components/KybCompanyStep.tsx`), which is what its
 * `submit` and `showSuccessToast` props exist for. A company is onboarded as a client or an organization from
 * `/app/clients`, a different domain with a different verification flow (KYB, not KYC), and those rows still
 * land in the identities table — so `/app/cards/identities` keeps listing business identities it can no
 * longer create.
 */
export const CreateHolderModal = ({ open, onOpenChange, onCreated }: CreateHolderModalProps) => {
  const { t } = useTranslations("cards");
  const [submitting, setSubmitting] = useState(false);

  const handleSuccess = () => {
    onCreated?.();
    onOpenChange(false);
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("create_holder_modal_title")}
      description={t("create_holder_modal_description")}
      size="lg"
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <ColocaButton variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("create_identity_modal_button_cancel")}
          </ColocaButton>
          <ColocaButton
            type="submit"
            variant="primary"
            form={PERSON_FORM_ID}
            loading={submitting}
            disabled={submitting}
          >
            {t("create_identity_modal_button_create")}
          </ColocaButton>
        </div>
      }
    >
      <PersonHolderForm
        formId={PERSON_FORM_ID}
        onSuccess={handleSuccess}
        onSubmittingChange={setSubmitting}
      />
    </ColocaDialog>
  );
};
